/**
 * Base layout for page filter bars: [filter icon] · [scrollable filter fields] · [Reset] [Apply].
 * Filters are always visible (not collapsible). Funnel icon and action buttons are vertically centered;
 * filter fields stay bottom-aligned so outlined labels sit correctly.
 */
import React, { ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  alpha,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { FILTER_UI_STANDARD } from '../../config/filterUiStandard';
import PageMarquee from '../common/PageMarquee';

export interface FilterStripLayoutProps {
  /** Page-specific filter controls (inputs, selects, pickers). */
  children: ReactNode;

  onClear?: () => void;
  onRefresh?: () => void;
  clearLabel?: string;
  refreshLabel?: string;
  refreshLoading?: boolean;
  refreshDisabled?: boolean;

  /** Replaces the default funnel icon (decorative). */
  filterToggle?: ReactNode;

  hasUnsavedChanges?: boolean;
  alert?: ReactNode;
  trailingActions?: ReactNode;

  dense?: boolean;
  elevation?: number;
}

const DEFAULT_CLEAR = 'Reset';
const DEFAULT_REFRESH = 'Apply';

const STRIP_BUTTON_SX = {
  fontSize: FILTER_UI_STANDARD.controlFontSize,
  lineHeight: 1.2,
  py: 0.35,
  px: 1.1,
  minHeight: 34,
  minWidth: 'auto',
  fontWeight: 600,
  textTransform: 'none' as const,
} as const;

const FilterStripLayout: React.FC<FilterStripLayoutProps> = ({
  children,
  onClear,
  onRefresh,
  clearLabel = DEFAULT_CLEAR,
  refreshLabel = DEFAULT_REFRESH,
  refreshLoading = false,
  refreshDisabled = false,
  filterToggle,
  hasUnsavedChanges = false,
  alert,
  trailingActions,
  dense = true,
  elevation = 1,
}) => {
  const gapFields = dense ? 2 : 2.5;
  const gapActions = dense
    ? FILTER_UI_STANDARD.filterActionButtonsGapDense
    : FILTER_UI_STANDARD.filterActionButtonsGap;
  const paperPy = dense ? 1.5 : 1.75;
  const paperPx = dense ? FILTER_UI_STANDARD.filterStripPaperPaddingXDense : FILTER_UI_STANDARD.filterStripPaperPaddingX;
  const innerPt = dense ? 1.25 : 1.5;
  const innerPb = dense ? 0.75 : 1;
  const gapIconToStrip = dense ? 1 : 1.25;

  const defaultFilterIcon = (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: SEARCE_COLORS.textSecondary,
        px: 0.25,
      }}
    >
      <FilterAltIcon sx={{ fontSize: '1.125rem' }} />
    </Box>
  );

  const actionButtons =
    (onClear != null || onRefresh != null) && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: gapActions,
          flexShrink: 0,
          alignItems: 'center',
          alignSelf: 'center',
          justifyContent: 'center',
        }}
      >
        {onClear != null && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestartAltIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={onClear}
            sx={{
              ...STRIP_BUTTON_SX,
              borderColor: SEARCE_COLORS.primary,
              color: SEARCE_COLORS.primary,
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
              },
            }}
          >
            {clearLabel}
          </Button>
        )}
        {onRefresh != null && (
          <Button
            variant="contained"
            size="small"
            startIcon={refreshLoading ? undefined : <RefreshIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={onRefresh}
            disabled={refreshDisabled || refreshLoading}
            sx={{
              ...STRIP_BUTTON_SX,
              backgroundColor: SEARCE_COLORS.primary,
              color: '#fff',
              '&:hover': { backgroundColor: SEARCE_COLORS.primary, opacity: 0.92 },
              '&:disabled': {
                backgroundColor: SEARCE_COLORS.neutral_300,
                color: SEARCE_COLORS.textSecondary,
              },
            }}
          >
            {refreshLoading ? 'Refreshing…' : refreshLabel}
          </Button>
        )}
        {trailingActions}
      </Box>
    );

  return (
    <>
    <PageMarquee />
    <Paper
      elevation={elevation}
      sx={{
        py: paperPy,
        px: paperPx,
        mb: 0,
        borderRadius: 1,
        border: `1px solid ${SEARCE_COLORS.border}`,
        backgroundColor: SEARCE_COLORS.white,
        overflow: 'visible',
        ...(elevation === 0 && { boxShadow: 'none' }),
      }}
    >
      {alert != null && <Box sx={{ mb: hasUnsavedChanges ? 1 : 1.25 }}>{alert}</Box>}

      {hasUnsavedChanges && (
        <Box
          sx={{
            mb: 1,
            p: dense ? 1 : 1.25,
            borderRadius: 1,
            backgroundColor: alpha(SEARCE_COLORS.info, 0.1),
            border: `1px solid ${alpha(SEARCE_COLORS.info, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: SEARCE_COLORS.info,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              flexShrink: 0,
            }}
          >
            i
          </Box>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.info_600 ?? SEARCE_COLORS.info_700, fontWeight: 500, fontSize: '0.8rem' }}>
            Filters have been changed. Click &apos;{refreshLabel}&apos; to apply.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'stretch',
          gap: gapIconToStrip,
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* Funnel: vertically centered in the strip only */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'center',
          }}
        >
          {filterToggle ?? defaultFilterIcon}
        </Box>

        {/* Filter fields: bottom-aligned only (not vertically centered) */}
        <Box
          sx={{
            flex: '1 1 0%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'flex-end',
            alignSelf: 'stretch',
            gap: gapFields,
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            pt: innerPt,
            pb: innerPb,
            '& > *': { minWidth: 0 },
            '& .MuiFormControl-root': { marginTop: 0, marginBottom: 0 },
            '& .MuiTextField-root': { marginTop: 0, marginBottom: 0 },
          }}
        >
          {children}
        </Box>

        {actionButtons}
      </Box>
    </Paper>
    </>
  );
};

export default FilterStripLayout;
